import React, { useState, useMemo } from 'react';
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import { 
  DataResult, 
  process, 
  State,
} from '@progress/kendo-data-query';

interface TelerikDatabaseGridProps {
  data: any[];
  columns: string[];
  tableName: string;
  onExport?: () => void;
}

const TelerikDatabaseGrid: React.FC<TelerikDatabaseGridProps> = ({ 
  data, 
  columns, 
  tableName, 
  onExport 
}) => {
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 50,
    sort: [],
    filter: undefined,
  });

  // Process data with current state
  const processedData = useMemo(() => {
    // Apply grid state (sorting, pagination)
    return process(data, dataState);
  }, [data, dataState]);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Kendo Grid */}
      <Grid
        data={processedData}
        {...dataState}
        onDataStateChange={handleDataStateChange}
        sortable={true}
        filterable={false}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 25, 50, 100],
          pageSizeValue: dataState.take
        }}
        style={{
          border: '1px solid #dee2e6',
          borderRadius: '0.5rem',
          overflow: 'hidden'
        }}
      >
        <GridToolbar>
          <div style={{ 
            padding: '0.5rem',
            fontWeight: 'bold',
            color: '#21433b',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6'
          }}>
            {tableName} - {processedData.total} total rows
          </div>
        </GridToolbar>
        
        {columns.map((column) => (
          <Column
            key={column}
            field={column}
            title={column}
            filterable={false}
            cell={(props) => {
              const value = props.dataItem[column];
              
              // Handle different data types
              if (value === null || value === undefined) {
                return (
                  <td>
                    <span style={{ color: '#999', fontStyle: 'italic' }}>NULL</span>
                  </td>
                );
              }
              
              if (typeof value === 'boolean') {
                return (
                  <td>
                    <span style={{ color: value ? '#4CAF50' : '#f44336' }}>
                      {value ? 'TRUE' : 'FALSE'}
                    </span>
                  </td>
                );
              }
              
              if (typeof value === 'number') {
                return (
                  <td>
                    <span style={{ fontFamily: 'monospace' }}>{value}</span>
                  </td>
                );
              }
              
              if (typeof value === 'string') {
                // Check if it's a date string
                const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
                if (dateRegex.test(value)) {
                  try {
                    const date = new Date(value);
                    return (
                      <td>
                        <span>{date.toLocaleString()}</span>
                      </td>
                    );
                  } catch {
                    return (
                      <td>
                        <span>{value}</span>
                      </td>
                    );
                  }
                }
                
                // Truncate long strings
                if (value.length > 100) {
                  return (
                    <td>
                      <span title={value}>
                        {value.substring(0, 100)}...
                      </span>
                    </td>
                  );
                }
              }
              
              return (
                <td>
                  <span>{String(value)}</span>
                </td>
              );
            }}
          />
        ))}
      </Grid>
    </div>
  );
};

export default TelerikDatabaseGrid;
